import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Form, Row, Col, Spin, Modal } from "antd";
import FormActions from "../../components/forms/FormActions";
import CheckBoxItem from "../../components/forms/CheckBoxItem";
import {
  BranchSelect,
  RolesSelect,
  EmployeesSelect,
} from "../../components/forms/AutoCompleteFormItem";

import Surface from "../../components/layouts/Surface";
import Centered from "../../components/layouts/Centered";
import { InputItem } from "../../components/forms/FormItems";
import PhoneNumberItem from "../../components/forms/PhoneNumberItem";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import DetailsHeader from "../../components/forms/DetailsHeader";
import Spacer from "../../components/layouts/Spacer";
import ResponsiveItemsWrapper from "../../components/layouts/ResponsiveItemsWrapper";
import AnimationWrapper from "../../components/animations/AnimationWrapper";
import {
  updateRecord,
  deleteRecord,
  loadRecord,
  activateUser,
  deactivateUser,
  reset,
} from "./userDetailsSlice";
import { fetchUserProfile } from "../user/UserSlice";
import TwoFactorAuth from "./components/TwoFactorAuth";
import { FormattedMessage } from "react-intl";
import { activationConfirmMessage } from "../../components/forms/formsFeedback";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
var flatten = require("flat");

const UserDetails = (props) => {
  const dispatch = useDispatch();
  const { mode } = props;
  const [form] = Form.useForm();
  const [allBranches, setAllBranches] = useState(false);
  const activeUser = useSelector((state) => state.userDetails.activeUser);
  const profileId = useSelector((state) => state.user.profile.user);
  const isAdmin = useSelector((state) => state.user.profile.admin);

  const fetchLoading = useSelector(
    (state) => state.pendingActions["userDetails/loadUser"]
  );
  const saveLoading = useSelector(
    (state) => state.pendingActions["userDetails/update"]
  );
  const deleteLoading = useSelector(
    (state) => state.pendingActions["userDetails/delete"]
  );

  let history = useHistory();
  const intl = useIntl();
  let { id } = useParams();
  const [modal, contextHolder] = Modal.useModal();

  const professionalFilter = {
    $and: [
      {
        $or: [
          { professional_role_id: 1 },
          { professional_role_id: 2 },
          { professional_role_id: 3 },
          { professional_role_id: 4 },
          { controller: true },
        ],
      },
      { $or: [{ status: 1 }, { status: 3 }] },
    ],
  };

  useEffect(() => {
    dispatch(loadRecord(id));
    return function cleanup() {
      dispatch(reset());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (mode === "edit" && activeUser) {
      const flat = flatten(activeUser);
      let role_id = null;
      let branches = [];
      if (activeUser.roles) {
        activeUser.roles.forEach((role) => {
          if (role.branch_id) {
            branches.push(role.branch_id);
          }
          role_id = role.role_id;
        });
      }
      setAllBranches(branches.length === 0);

      form.setFieldsValue({
        ...flat,
        created_at: moment(flat["created_at"]).format("LLL"),
        role_id: role_id,
        branches: branches,
        all_branches: branches.length === 0,
      });
    }
  }, [activeUser, form, mode]);

  const onFinish = async (values) => {
    let updated = {
      id: activeUser.id,
      name: values.name,
      phone_number: values.phone_number,
      user_name: values.user_name,
      associated_employee_id: values.associated_employee_id,
      roles: [],
      has_mobile_access: values.has_mobile_access,
      has_desktop_access: values.has_desktop_access,
      has_minimum_mobile_access: values.has_minimum_mobile_access,
    };
    if (values.branches) {
      values.branches.forEach((branch) => {
        updated["roles"].push({
          branch_id: branch,
          role_id: values.role_id,
        });
      });
    } else if (allBranches) {
      updated["roles"].push({
        branch_id: null,
        role_id: values.role_id,
      });
    }
    const action = await dispatch(updateRecord(updated));
    if (!action.error) {
      if (activeUser.id === profileId) {
        dispatch(fetchUserProfile());
      }

      history.goBack();
    }
  };

  const onActivate = (checked) => {
    activationConfirmMessage(
      activeUser,
      intl,
      checked,
      activeUser.name,
      "users",
      onActivateConfirmed,
      modal
    );
  };

  const onActivateConfirmed = (record) => {
    const toDispatch = record.active ? deactivateUser : activateUser;
    return dispatch(toDispatch(record));
  };

  const onConfirm = async () => {
    const action = await dispatch(deleteRecord(activeUser.id));
    if (!action.error) {
      history.goBack();
    }
  };

  const onDecline = () => {
    history.goBack();
  };

  const onCheckboxChange = (e) => {
    setAllBranches(e.target.checked);
  };

  return fetchLoading ? (
    <Centered style={{ height: "448px" }}>
      <Spin size="large" />
    </Centered>
  ) : (
    <AnimationWrapper duration={0.6} overflow={true}>
      <Form layout="vertical" onFinish={onFinish} form={form}>
        {contextHolder}

        <Surface>
          <DetailsHeader
            resource={"users"}
            action={"editor"}
            editTitle={<FormattedMessage id="app.forms.editUserTitle" />}
            hasSwitch={true}
            mode={"edit"}
            isActive={activeUser ? activeUser.active : false}
            onActivate={onActivate}
          />
          <Row>
            <Col xs={24} sm={24} md={24} lg={21} xl={15} xxl={10}>
              <ResponsiveItemsWrapper>
                <div style={{ flex: 1 }}>
                  <InputItem name="name" category="user" />
                </div>

                <Spacer horizontal={true} />
                <div style={{ flex: 1 }}>
                  <InputItem name="user_name" category="user" disabled={true} />
                </div>
              </ResponsiveItemsWrapper>

              <ResponsiveItemsWrapper>
                <div style={{ flex: 1 }}>
                  <PhoneNumberItem name="phone_number" category="user" />
                </div>

                <Spacer horizontal={true} />
                <div style={{ flex: 1 }}>
                  <RolesSelect name="role_id" required={true} category="user" />
                </div>
              </ResponsiveItemsWrapper>

              <ResponsiveItemsWrapper>
                <div style={{ flex: 1 }}>
                  <CheckBoxItem
                    showLabel={true}
                    name="all_branches"
                    category="user"
                    onChange={onCheckboxChange}
                  />
                </div>

                <Spacer horizontal={true} />
                <div style={{ flex: 1 }}>
                  {!allBranches ? (
                    <BranchSelect
                      name="branches"
                      category="user"
                      mode="multiple"
                      required={true}
                    />
                  ) : null}
                </div>
              </ResponsiveItemsWrapper>
              <ResponsiveItemsWrapper>
                <div style={{ flex: 1 }}>
                  <CheckBoxItem
                    showLabel={true}
                    name="has_desktop_access"
                    category="user"
                  />
                </div>

                <Spacer horizontal={true} />
                <div style={{ flex: 1 }}>
                  <CheckBoxItem
                    showLabel={true}
                    name="has_mobile_access"
                    category="user"
                  />
                </div>
              </ResponsiveItemsWrapper>
              <ResponsiveItemsWrapper>
                <div style={{ flex: 1 }}>
                  <CheckBoxItem
                    showLabel={true}
                    name="has_minimum_mobile_access"
                    category="user"
                  />
                </div>

                <Spacer horizontal={true} />
                <div style={{ flex: 1 }}>
                  {activeUser.id && (
                    <EmployeesSelect
                      name="associated_employee_id"
                      category="user"
                      waitForFetch={!activeUser.id}
                      extraParams={{
                        filter: professionalFilter,
                      }}
                      proffesional={true}
                    />
                  )}
                </div>
              </ResponsiveItemsWrapper>
            </Col>
          </Row>
          <TwoFactorAuth />
        </Surface>
        <FormActions
          resource={"users"}
          action={"editor"}
          onCancel={onDecline}
          category={"user"}
          mode={mode}
          record={activeUser}
          onDecline={onDecline}
          onConfirm={onConfirm}
          saveLoading={saveLoading}
          deleteLoading={deleteLoading}
        />
      </Form>
    </AnimationWrapper>
  );
};

UserDetails.propTypes = {
  mode: PropTypes.string,
};

export default UserDetails;
